/**
 * Watchlist Alert Service
 * Persists per-property investor alert configs to Supabase so the
 * monitor-watchlist-alerts edge function can poll and send SMS/email.
 */

import { supabase } from '../lib/supabase';

// ─── Upsert alert config for a watchlisted property ──────────────────────────

export const upsertWatchlistAlert = async (userId, propertyId, alertConfig, currentPrice) => {
  if (!userId || !propertyId) return { data: null, error: 'Missing userId or propertyId' };
  try {
    const { data, error } = await supabase
      ?.from('watchlist_property_alerts')
      ?.upsert(
        {
          user_id: userId,
          property_id: propertyId,
          alert_config: alertConfig,
          is_active: alertConfig?.enabled !== false,
          last_known_price: currentPrice || null,
          updated_at: new Date()?.toISOString(),
        },
        { onConflict: 'user_id,property_id' }
      )
      ?.select()
      ?.single();

    if (error) return { data: null, error: error?.message };
    return { data, error: null };
  } catch (err) {
    return { data: null, error: err?.message || 'Failed to upsert watchlist alert' };
  }
};

// ─── Deactivate alert for a property ─────────────────────────────────────────

export const deactivateWatchlistAlert = async (userId, propertyId) => {
  if (!userId || !propertyId) return { error: 'Missing userId or propertyId' };
  try {
    const { error } = await supabase
      ?.from('watchlist_property_alerts')
      ?.update({ is_active: false, updated_at: new Date()?.toISOString() })
      ?.eq('user_id', userId)
      ?.eq('property_id', propertyId);

    if (error) return { error: error?.message };
    return { error: null };
  } catch (err) {
    return { error: err?.message || 'Failed to deactivate alert' };
  }
};

// ─── Trigger the monitoring edge function (manual / on-demand) ───────────────

export const triggerWatchlistAlertMonitor = async (propertyId = null) => {
  try {
    const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;
    const SUPABASE_ANON_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY;
    if (!SUPABASE_URL) return;

    const { data: sessionData } = await supabase?.auth?.getSession();
    const accessToken = sessionData?.session?.access_token || SUPABASE_ANON_KEY;

    await fetch(`${SUPABASE_URL}/functions/v1/monitor-watchlist-alerts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(propertyId ? { property_id: propertyId } : {}),
    });
  } catch (err) {
    console.warn('[WatchlistAlerts] Monitor trigger failed silently:', err?.message);
  }
};
